import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, Clock, Phone, Mail, MapPin, Coins, Share2, FileText,
  Plus, RefreshCw, Filter, Pencil, X, Check,
  MessageSquare, MoreHorizontal, Calendar, Activity, FolderOpen,
  FolderPlus, Archive, ArchiveRestore, ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import NuovaRichiestaDialog from "../components/ospiti-rete/NuovaRichiestaDialog";
import RichiediDocumentiDialog from "../components/ospiti-rete/RichiediDocumentiDialog";
import CondividiReteDialog from "../components/ospiti-rete/CondividiReteDialog";
import ImportaEmailDialog from "../components/ospiti-rete/ImportaEmailDialog";
import ConfermaIngressoDialog from "../components/ospiti-rete/ConfermaIngressoDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI = ["Tutti", "Nuova", "Da seguire", "Documenti richiesti", "Condiviso in rete", "Inserito", "Chiuso"];

const STATO_COLORS = {
  "Nuova":                "bg-sky-100 text-sky-700 border border-sky-200",
  "Da seguire":           "bg-amber-100 text-amber-700 border border-amber-200",
  "Documenti richiesti":  "bg-violet-100 text-violet-700 border border-violet-200",
  "Condiviso in rete":    "bg-blue-100 text-blue-700 border border-blue-200",
  "Inserito":             "bg-emerald-100 text-emerald-700 border border-emerald-200",
  "Chiuso":               "bg-slate-100 text-slate-500 border border-slate-200",
};

const ATTIVITA_ICONS = {
  "richiesta inserita":          <Plus className="w-3.5 h-3.5 text-blue-500" />,
  "telefonata effettuata":       <Phone className="w-3.5 h-3.5 text-green-500" />,
  "email inviata":               <Mail className="w-3.5 h-3.5 text-blue-500" />,
  "whatsapp inviato":            <MessageSquare className="w-3.5 h-3.5 text-green-600" />,
  "richiesta documenti inviata": <FileText className="w-3.5 h-3.5 text-violet-500" />,
  "link documenti aperto":       <FolderOpen className="w-3.5 h-3.5 text-amber-500" />,
  "documento caricato":          <FileText className="w-3.5 h-3.5 text-emerald-500" />,
  "richiesta condivisa in rete": <Share2 className="w-3.5 h-3.5 text-blue-500" />,
  "stato modificato":            <Activity className="w-3.5 h-3.5 text-slate-500" />,
  "gettone maturato":            <Coins className="w-3.5 h-3.5 text-amber-500" />,
  "gettone saldato":             <Check className="w-3.5 h-3.5 text-emerald-500" />,
  "memorandum aggiornato":       <Calendar className="w-3.5 h-3.5 text-violet-500" />,
  "fascicolo creato":            <FolderPlus className="w-3.5 h-3.5 text-emerald-600" />,
  "richiesta archiviata":        <Archive className="w-3.5 h-3.5 text-slate-500" />,
  "richiesta ripristinata":      <ArchiveRestore className="w-3.5 h-3.5 text-blue-500" />,
};

function StatoBadge({ stato }) {
  const cls = STATO_COLORS[stato] || "bg-slate-100 text-slate-500 border border-slate-200";
  return <Badge className={`${cls} font-medium text-xs`}>{stato || "Nuova"}</Badge>;
}

function formatDate(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return dt;
  }
}

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dt;
  }
}

function KpiCard({ label, value, icon: Icon, color = "text-slate-700", bg = "bg-white" }) {
  return (
    <Card className={`card-base ${bg}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-slate-100`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Pannello dettaglio richiesta ───────────────────────────────────────────

function PannelloDettaglio({
  richiesta, onClose, token, onUpdate, onRichiediDocumenti, onCondividi,
  onConfermaIngresso, onArchivia, onRipristina, canCreateFascicolo, vistaArchiviate,
}) {
  const [tab, setTab] = useState("informazioni");
  const [attivita, setAttivita] = useState([]);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [editMemo, setEditMemo] = useState(false);
  const [memo, setMemo] = useState(richiesta?.memorandum || {});
  const [savingMemo, setSavingMemo] = useState(false);
  const [nuovaAtt, setNuovaAtt] = useState({ tipo: "telefonata effettuata", testo: "" });
  const [addingAtt, setAddingAtt] = useState(false);
  const [showModificaDialog, setShowModificaDialog] = useState(false);
  const [docLink, setDocLink] = useState(null);
  const [loadingDocLink, setLoadingDocLink] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };
  const rid = richiesta?.id;

  useEffect(() => {
    setMemo(richiesta?.memorandum || {});
    setEditMemo(false);
    setShowModificaDialog(false);
    setTab("informazioni");
    setDocLink(null);
  }, [richiesta?.id]);

  const loadAttivita = useCallback(async () => {
    if (!rid) return;
    setLoadingAtt(true);
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/richieste/${rid}/attivita`, { headers: authHeader });
      setAttivita(res.data.attivita || []);
    } catch {
      toast.error("Errore nel caricamento attività");
    } finally {
      setLoadingAtt(false);
    }
  }, [rid]);

  useEffect(() => {
    if (tab === "attivita") loadAttivita();
  }, [tab, loadAttivita]);

  const loadDocLink = useCallback(async () => {
    if (!rid) return;
    setLoadingDocLink(true);
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/richieste/${rid}/documento-link`, { headers: authHeader });
      setDocLink(res.data.link || null);
    } catch {
      setDocLink(null);
    } finally {
      setLoadingDocLink(false);
    }
  }, [rid, token]);

  useEffect(() => {
    if (tab === "documenti") loadDocLink();
  }, [tab, loadDocLink]);

  const saveMemo = async () => {
    setSavingMemo(true);
    try {
      await axios.put(`${API_URL}/api/ospiti-rete/richieste/${rid}/memorandum`, memo, { headers: authHeader });
      toast.success("Memorandum aggiornato");
      setEditMemo(false);
      onUpdate?.();
    } catch {
      toast.error("Errore nel salvataggio memorandum");
    } finally {
      setSavingMemo(false);
    }
  };

  const scaricaDocumento = async (doc) => {
    setDownloadingDocId(doc.file_id);
    try {
      const res = await axios.get(
        `${API_URL}/api/ospiti-rete/richieste/${rid}/documenti/${doc.file_id}/download`,
        { headers: authHeader, responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename_originale || doc.label || doc.doc_key || "documento";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nel download del documento");
    } finally {
      setDownloadingDocId(null);
    }
  };

  const aggiungiAttivita = async () => {
    if (!nuovaAtt.testo.trim()) { toast.error("Inserisci una descrizione"); return; }
    setAddingAtt(true);
    try {
      await axios.post(`${API_URL}/api/ospiti-rete/richieste/${rid}/attivita`, nuovaAtt, { headers: authHeader });
      toast.success("Attività registrata");
      setNuovaAtt({ tipo: "telefonata effettuata", testo: "" });
      loadAttivita();
      onUpdate?.();
    } catch {
      toast.error("Errore registrazione attività");
    } finally {
      setAddingAtt(false);
    }
  };

  if (!richiesta) return null;

  const nomeRef = `${richiesta.nome_referente} ${richiesta.cognome_referente}`.trim();

  return (
    <div className="h-full flex flex-col bg-white border-l border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
              {(richiesta.nome_referente?.[0] || "?").toUpperCase()}{(richiesta.cognome_referente?.[0] || "").toUpperCase()}
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-base leading-tight">{nomeRef}</h2>
              {richiesta.nome_ospite && (
                <p className="text-xs text-slate-500">Ospite: {richiesta.nome_ospite}{richiesta.eta_ospite ? `, ${richiesta.eta_ospite} anni` : ""}</p>
              )}
              {richiesta.telefono && (
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <Phone className="w-3 h-3" /> {richiesta.telefono}
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Stato */}
        <div className="flex items-center gap-2 mt-3">
          <StatoBadge stato={richiesta.stato} />
        </div>

        {/* Azioni rapide */}
        <div className="flex flex-wrap gap-2 mt-3">
          {richiesta.telefono && (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" asChild>
              <a href={`tel:${richiesta.telefono}`}>
                <Phone className="w-3.5 h-3.5" />Chiama
              </a>
            </Button>
          )}
          {richiesta.telefono && (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-green-200 text-green-700 hover:bg-green-50" asChild>
              <a href={`https://wa.me/${richiesta.telefono?.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">
                <MessageSquare className="w-3.5 h-3.5" />WhatsApp
              </a>
            </Button>
          )}
          {!vistaArchiviate && (
            <>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => onRichiediDocumenti?.(richiesta)}>
            <FileText className="w-3.5 h-3.5" />Richiedi documenti
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => onCondividi?.(richiesta)}>
            <Share2 className="w-3.5 h-3.5" />Condividi in rete
          </Button>
            </>
          )}
          {richiesta.fascicolo_id ? (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" asChild>
              <Link to={`/fascicoli-assistenziali/${richiesta.fascicolo_id}`}>
                <ExternalLink className="w-3.5 h-3.5" />Apri fascicolo
              </Link>
            </Button>
          ) : canCreateFascicolo && !vistaArchiviate ? (
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onConfermaIngresso?.(richiesta)}>
              <FolderPlus className="w-3.5 h-3.5" />Conferma ingresso e crea fascicolo
            </Button>
          ) : null}
          {vistaArchiviate ? (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => onRipristina?.(richiesta)}>
              <ArchiveRestore className="w-3.5 h-3.5" />Ripristina
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => onArchivia?.(richiesta)}>
              <Archive className="w-3.5 h-3.5" />Archivia
            </Button>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-slate-100">
          <Button
            size="sm"
            variant="outline"
            className="w-full h-8 text-xs"
            onClick={() => setShowModificaDialog(true)}
          >
            Modifica
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-y-auto">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full rounded-none border-b border-slate-100 bg-white h-9 justify-start px-4 gap-0">
            {["informazioni", "attivita", "documenti", "memorandum"].map((t) => (
              <TabsTrigger key={t} value={t} className="text-xs capitalize h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Informazioni */}
          <TabsContent value="informazioni" className="p-4 space-y-3 m-0">
            {richiesta.fascicolo_id && (
              <InfoRow label="Fascicolo" value={richiesta.stato_ospite_fascicolo === "PRE_INGRESSO" ? "Pre-ingresso" : "Creato"} />
            )}
            <InfoRow label="Fonte" value={richiesta.fonte} />
            <InfoRow label="Email referente" value={richiesta.email_referente} />
            <InfoRow label="Relazione" value={richiesta.relazione_ospite} />
            {richiesta.nome_ospite && <>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">Ospite</p>
              </div>
              <InfoRow label="Nome ospite" value={richiesta.nome_ospite} />
              <InfoRow label="Età" value={richiesta.eta_ospite} />
              <InfoRow label="Sesso" value={richiesta.sesso_ospite} />
              <InfoRow label="Autosufficienza" value={richiesta.autosufficienza} />
              <InfoRow label="Urgenza" value={richiesta.urgenza_inserimento} />
              <InfoRow label="Tipo posto" value={richiesta.tipo_posto} />
              <InfoRow label="Patologie" value={richiesta.patologie} />
            </>}
            {(richiesta.zona || richiesta.budget) && <>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">Info richiesta</p>
              </div>
              <InfoRow label="Zona" value={richiesta.zona} />
              <InfoRow label="Budget" value={richiesta.budget} />
            </>}
            {richiesta.note && <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-line">{richiesta.note}</div>}
            {/* Gettone */}
            {richiesta.condiviso_in_rete && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">Gettone</p>
                <InfoRow label="Gettone previsto" value={richiesta.gettone_previsto != null ? `${richiesta.gettone_previsto} €` : null} />
                <InfoRow label="Stato gettone" value={richiesta.stato_gettone} />
                <InfoRow label="Data maturazione" value={formatDate(richiesta.data_maturazione)} />
                <InfoRow label="Data saldo" value={formatDate(richiesta.data_saldo)} />
              </div>
            )}
          </TabsContent>

          {/* Attività */}
          <TabsContent value="attivita" className="p-4 m-0 space-y-4">
            {/* Form nuova attività */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <Label className="text-xs">Registra attività</Label>
              <Select value={nuovaAtt.tipo} onValueChange={(v) => setNuovaAtt((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["telefonata effettuata", "email inviata", "whatsapp inviato", "nota aggiunta"].map((t) => (
                    <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="text-xs h-8"
                placeholder="Descrizione (es. Chiamato il familiare, in attesa risposta)"
                value={nuovaAtt.testo}
                onChange={(e) => setNuovaAtt((p) => ({ ...p, testo: e.target.value }))}
              />
              <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={aggiungiAttivita} disabled={addingAtt}>
                {addingAtt ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
                Registra
              </Button>
            </div>
            {/* Timeline */}
            {loadingAtt ? (
              <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 text-slate-400 animate-spin" /></div>
            ) : (
              <div className="space-y-2">
                {attivita.map((a) => (
                  <div key={a.id} className="flex gap-2.5 text-xs">
                    <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                      {ATTIVITA_ICONS[a.tipo] || <Activity className="w-3 h-3 text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700">{a.testo}</p>
                      <p className="text-slate-400 mt-0.5">{formatDateTime(a.created_at)}{a.operatore ? ` · ${a.operatore}` : ""}</p>
                    </div>
                  </div>
                ))}
                {attivita.length === 0 && (
                  <p className="text-slate-400 text-center py-4">Nessuna attività registrata</p>
                )}
              </div>
            )}
          </TabsContent>

          {/* Documenti */}
          <TabsContent value="documenti" className="p-4 m-0">
            <div className="space-y-2">
              {loadingDocLink ? (
                <p className="text-xs text-slate-400 text-center py-2">Caricamento stato richiesta documenti...</p>
              ) : docLink ? (() => {
                const richiesti = docLink.documenti_richiesti || [];
                const ricevuti = richiesti.filter((d) => d.stato === "ricevuto").length;
                const totale = richiesti.length;
                if (docLink.completato) {
                  return (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                      Documentazione confermata dal familiare
                      {docLink.completato_at ? ` il ${formatDate(docLink.completato_at)}` : ""}.
                    </div>
                  );
                }
                if (totale > 0 && ricevuti === totale) {
                  return (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                      Tutti i documenti ({ricevuti}/{totale}) sono stati caricati: in attesa della conferma definitiva dal familiare sul portale.
                    </div>
                  );
                }
                if (totale > 0) {
                  return (
                    <div className="p-3 rounded-lg bg-violet-50 border border-violet-200 text-xs text-violet-800">
                      Documenti caricati: {ricevuti}/{totale}. Lo stato resta «Documenti richiesti» finché il familiare non conferma l&apos;invio.
                    </div>
                  );
                }
                return null;
              })() : null}
              {(richiesta.documenti_ricevuti || []).length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">Nessun documento ricevuto</p>
              ) : (
                (richiesta.documenti_ricevuti || []).map((doc) => (
                  <div key={doc.file_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-700">{doc.label || doc.doc_key}</p>
                      <p className="text-xs text-slate-400">
                        {formatDate(doc.uploaded_at)}
                        {doc.sezione_fascicolo || doc.sezione
                          ? ` · ${{ anagrafica: "Anagrafica", sanitaria: "Sanitaria", legali: "Documenti legali", generale: "Generale", altri: "Generale", amministrativa: "Amministrativa" }[doc.sezione_fascicolo || doc.sezione] || doc.sezione}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => scaricaDocumento(doc)}
                      disabled={downloadingDocId === doc.file_id}
                    >
                      {downloadingDocId === doc.file_id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        "Scarica"
                      )}
                    </Button>
                  </div>
                ))
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
                onClick={() => onRichiediDocumenti?.(richiesta)}
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Richiedi documenti
              </Button>
            </div>
          </TabsContent>

          {/* Memorandum */}
          <TabsContent value="memorandum" className="p-4 m-0">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-amber-900 text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Memorandum interno
                </h4>
                {!editMemo && (
                  <button onClick={() => setEditMemo(true)} className="text-amber-600 hover:text-amber-800 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </div>

              {editMemo ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Prossima attività</Label>
                    <Select value={memo.attivita || ""} onValueChange={(v) => setMemo((p) => ({ ...p, attivita: v }))}>
                      <SelectTrigger className="h-8 text-xs mt-1">
                        <SelectValue placeholder="Seleziona attività" />
                      </SelectTrigger>
                      <SelectContent>
                        {["Richiamare", "Inviare documenti", "Attendere risposta", "Verificare disponibilità stanza", "Ricontattare familiare", "Inviare preventivo", "Fissare visita"].map((v) => (
                          <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Quando</Label>
                    <Input
                      type="datetime-local"
                      className="h-8 text-xs mt-1"
                      value={memo.quando ? memo.quando.slice(0, 16) : ""}
                      onChange={(e) => setMemo((p) => ({ ...p, quando: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Assegnato a</Label>
                    <Input className="h-8 text-xs mt-1" value={memo.assegnato_a || ""} onChange={(e) => setMemo((p) => ({ ...p, assegnato_a: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Note</Label>
                    <Textarea className="text-xs mt-1" rows={2} value={memo.note || ""} onChange={(e) => setMemo((p) => ({ ...p, note: e.target.value }))} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={saveMemo} disabled={savingMemo}>
                      {savingMemo ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}Salva
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setMemo(richiesta.memorandum || {}); setEditMemo(false); }}>
                      Annulla
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <InfoRow label="Attività" value={memo.attivita} />
                  <InfoRow label="Quando" value={memo.quando ? formatDateTime(memo.quando) : null} />
                  <InfoRow label="Assegnato a" value={memo.assegnato_a} />
                  {memo.note && <p className="text-slate-700 text-xs mt-2 whitespace-pre-line">{memo.note}</p>}
                  {!memo.attivita && !memo.quando && !memo.note && (
                    <button onClick={() => setEditMemo(true)} className="text-amber-700 text-xs hover:underline">
                      + Aggiungi memorandum
                    </button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <NuovaRichiestaDialog
        open={showModificaDialog}
        onOpenChange={setShowModificaDialog}
        richiesta={richiesta}
        onUpdated={() => onUpdate?.()}
      />
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value || value === "—") return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-400 w-28 shrink-0">{label}</span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  );
}

// ─── Pagina principale ─────────────────────────────────────────────────────

export default function OspitiInRete() {
  const { token, activeStruttura, hasPermission } = useAuth();
  const navigate = useNavigate();
  const authHeader = { Authorization: `Bearer ${token}` };
  const canCreateFascicolo = hasPermission("fascicolo:create");

  const [richieste, setRichieste] = useState([]);
  const [kpi, setKpi] = useState({ richieste_ricevute: 0, da_seguire_oggi: 0, inseriti: 0, condivisi_in_rete: 0, gettoni_maturati: 0, archiviate: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRichiesta, setSelectedRichiesta] = useState(null);
  const [filtroStato, setFiltroStato] = useState("Tutti");
  const [vistaArchiviate, setVistaArchiviate] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showNuovaDialog, setShowNuovaDialog] = useState(false);
  const [showRichiediDocDialog, setShowRichiediDocDialog] = useState(false);
  const [showCondividiDialog, setShowCondividiDialog] = useState(false);
  const [showImportaEmailDialog, setShowImportaEmailDialog] = useState(false);
  const [showConfermaIngressoDialog, setShowConfermaIngressoDialog] = useState(false);

  const fetchKpi = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/kpi`, { headers: authHeader });
      setKpi(res.data);
    } catch { /* silent */ }
  }, [token, activeStruttura?.id]);

  const fetchRichieste = useCallback(async (resetPage = false) => {
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 10 });
      if (filtroStato !== "Tutti") params.set("stato", filtroStato);
      if (q) params.set("q", q);
      if (vistaArchiviate) params.set("archiviate", "true");
      const res = await axios.get(`${API_URL}/api/ospiti-rete/richieste?${params}`, { headers: authHeader });
      setRichieste(res.data.richieste || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch {
      toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
  }, [token, filtroStato, q, page, activeStruttura?.id, vistaArchiviate]);

  useEffect(() => {
    fetchKpi();
    fetchRichieste(true);
  }, [filtroStato, activeStruttura?.id, vistaArchiviate]);

  useEffect(() => {
    fetchRichieste();
  }, [page, activeStruttura?.id]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRichieste(true);
  };

  const handleSelect = async (r) => {
    if (selectedId === r.id) {
      setSelectedId(null);
      setSelectedRichiesta(null);
      return;
    }
    setSelectedId(r.id);
    setSelectedRichiesta(r);
    // Carica la versione completa con documenti
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/richieste/${r.id}`, { headers: authHeader });
      setSelectedRichiesta(res.data);
    } catch { /* use local */ }
  };

  const handleUpdate = () => {
    fetchKpi();
    fetchRichieste();
    if (selectedId) {
      axios.get(`${API_URL}/api/ospiti-rete/richieste/${selectedId}`, { headers: authHeader })
        .then((res) => setSelectedRichiesta(res.data))
        .catch(() => {});
    }
  };

  const handleCreated = (nuova) => {
    fetchKpi();
    fetchRichieste(true);
    setSelectedId(nuova.id);
    setSelectedRichiesta(nuova);
  };

  const handleArchivia = async (r) => {
    const target = r || selectedRichiesta;
    if (!target?.id) return;
    try {
      await axios.post(`${API_URL}/api/ospiti-rete/richieste/${target.id}/archivia`, {}, { headers: authHeader });
      toast.success("Richiesta archiviata");
      setSelectedId(null);
      setSelectedRichiesta(null);
      fetchKpi();
      fetchRichieste(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'archiviazione");
    }
  };

  const handleRipristina = async (r) => {
    const target = r || selectedRichiesta;
    if (!target?.id) return;
    try {
      await axios.post(`${API_URL}/api/ospiti-rete/richieste/${target.id}/ripristina`, {}, { headers: authHeader });
      toast.success("Richiesta ripristinata");
      setVistaArchiviate(false);
      setSelectedId(target.id);
      fetchKpi();
      fetchRichieste(true);
      const res = await axios.get(`${API_URL}/api/ospiti-rete/richieste/${target.id}`, { headers: authHeader });
      setSelectedRichiesta(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel ripristino");
    }
  };

  const switchVista = (archiviate) => {
    setVistaArchiviate(archiviate);
    setFiltroStato("Tutti");
    setPage(1);
    setSelectedId(null);
    setSelectedRichiesta(null);
  };

  return (
    <div className="p-4 md:p-6 w-full max-w-none">
      <PageHeader
        title="Ospiti in Rete"
        subtitle="Richieste posto letto e condivisioni"
        icon={Users}
        iconContainerClassName="bg-gradient-to-br from-blue-500 to-blue-600"
        iconClassName="text-white"
      />

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <KpiCard label="Richieste ricevute" value={kpi.richieste_ricevute} icon={Users} color="text-blue-600" />
        <KpiCard label="Da seguire oggi" value={kpi.da_seguire_oggi} icon={Clock} color="text-amber-600" />
        <KpiCard label="Inseriti" value={kpi.inseriti} icon={Check} color="text-emerald-600" />
        <KpiCard label="Condivisi in rete" value={kpi.condivisi_in_rete} icon={Share2} color="text-blue-500" />
        <KpiCard label="Gettoni maturati" value={`${kpi.gettoni_maturati ?? 0} €`} icon={Coins} color="text-amber-500" />
      </div>

      {/* Azioni + filtri */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {!vistaArchiviate && (
          <>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" onClick={() => setShowNuovaDialog(true)}>
              <Plus className="w-4 h-4" />Nuova richiesta
            </Button>
            <Button variant="outline" className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setShowRichiediDocDialog(true)} disabled={!selectedRichiesta}>
              <FileText className="w-4 h-4" />Richiedi documenti
            </Button>
            <Button variant="outline" className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => setShowCondividiDialog(true)} disabled={!selectedRichiesta}>
              <Share2 className="w-4 h-4" />Condividi in rete
            </Button>
            <Button variant="outline" className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => setShowImportaEmailDialog(true)}>
              <Mail className="w-4 h-4" />Importa da email
            </Button>
            {canCreateFascicolo && (
              <Button
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setShowConfermaIngressoDialog(true)}
                disabled={!selectedRichiesta || !!selectedRichiesta.fascicolo_id}
              >
                <FolderPlus className="w-4 h-4" />Conferma ingresso e crea fascicolo
              </Button>
            )}
          </>
        )}
        <form onSubmit={handleSearch} className="flex gap-2 ml-auto">
          <Input
            className="h-9 w-52 text-sm"
            placeholder="Cerca referente, ospite..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button type="submit" variant="outline" size="icon" className="h-9 w-9">
            <Filter className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Tab attive / archiviate + filtro stati */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button
            type="button"
            onClick={() => switchVista(false)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${
              !vistaArchiviate ? "bg-blue-600 text-white" : "text-slate-600 hover:text-blue-700"
            }`}
          >
            Attive
          </button>
          <button
            type="button"
            onClick={() => switchVista(true)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium ${
              vistaArchiviate ? "bg-slate-700 text-white" : "text-slate-600 hover:text-slate-800"
            }`}
          >
            Archiviate{typeof kpi.archiviate === "number" ? ` (${kpi.archiviate})` : ""}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATI.map((s) => (
            <button
              key={s}
              onClick={() => setFiltroStato(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                filtroStato === s
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Layout principale: lista + pannello dettaglio */}
      <div className="flex gap-4">
        {/* Lista */}
        <div className={`flex-1 min-w-0 ${selectedRichiesta ? "hidden lg:block" : ""}`}>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : richieste.length === 0 ? (
            <Card className="card-base">
              <CardContent className="p-10 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  {vistaArchiviate ? "Nessuna richiesta archiviata" : "Nessuna richiesta trovata"}
                </p>
                {!vistaArchiviate && (
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white gap-1.5" onClick={() => setShowNuovaDialog(true)}>
                  <Plus className="w-4 h-4" />Nuova richiesta
                </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Header colonne */}
              <div className="hidden md:grid grid-cols-[1fr_160px_140px_180px_32px] gap-3 px-4 text-xs text-slate-400 font-medium uppercase tracking-wide">
                <span>Richiesta</span>
                <span>Fonte</span>
                <span>Stato</span>
                <span>Prossima attività</span>
                <span></span>
              </div>

              {richieste.map((r) => (
                <Card
                  key={r.id}
                  className={`card-base cursor-pointer transition-all ${
                    selectedId === r.id
                      ? "border-blue-400 shadow-md ring-1 ring-blue-200"
                      : "hover:shadow-md hover:border-blue-200"
                  }`}
                  onClick={() => handleSelect(r)}
                >
                  <CardContent className="p-3 md:p-4">
                    <div className="flex flex-col md:grid md:grid-cols-[1fr_160px_140px_180px_32px] md:items-center gap-2 md:gap-3">
                      {/* Referente */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs shrink-0">
                          {(r.nome_referente?.[0] || "?").toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">
                            {r.nome_referente} {r.cognome_referente}
                          </p>
                          {r.nome_ospite ? (
                            <p className="text-xs text-slate-500 truncate">Ospite: {r.nome_ospite}</p>
                          ) : (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{r.telefono}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Fonte */}
                      <span className="text-xs text-slate-500 truncate hidden md:block">{r.fonte || "—"}</span>

                      {/* Stato */}
                      <div className="flex md:block items-center gap-1.5">
                        <StatoBadge stato={r.stato} />
                        {r.fascicolo_id && (
                          <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 ml-0 md:mt-1 md:inline-block">
                            Fascicolo
                          </span>
                        )}
                      </div>

                      {/* Prossima attività */}
                      <div className="hidden md:block">
                        {r.memorandum?.attivita ? (
                          <div>
                            <p className="text-xs text-slate-700 font-medium">{r.memorandum.attivita}</p>
                            {r.memorandum.quando && (
                              <p className="text-xs text-slate-400">{formatDateTime(r.memorandum.quando)}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </div>

                      {/* Azioni rapide */}
                      <div className="hidden md:flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-sm">
                            {!vistaArchiviate && (
                              <>
                                {canCreateFascicolo && !r.fascicolo_id && (
                                  <DropdownMenuItem onSelect={() => { setSelectedId(r.id); setSelectedRichiesta(r); setShowConfermaIngressoDialog(true); }}>
                                    Conferma ingresso e crea fascicolo
                                  </DropdownMenuItem>
                                )}
                                {r.fascicolo_id && (
                                  <DropdownMenuItem onSelect={() => navigate(`/fascicoli-assistenziali/${r.fascicolo_id}`)}>
                                    Apri fascicolo
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onSelect={() => { setSelectedId(r.id); setSelectedRichiesta(r); setShowRichiediDocDialog(true); }}>
                                  Richiedi documenti
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setSelectedId(r.id); setSelectedRichiesta(r); setShowCondividiDialog(true); }}>
                                  Condividi in rete
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleArchivia(r)}>
                                  Archivia
                                </DropdownMenuItem>
                              </>
                            )}
                            {vistaArchiviate && (
                              <DropdownMenuItem onSelect={() => handleRipristina(r)}>
                                Ripristina
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Paginazione */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-slate-400">{total} richieste totali</p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === 1} onClick={() => setPage(page - 1)}>
                      ←
                    </Button>
                    <span className="text-xs text-slate-500 px-2 flex items-center">
                      {page} / {totalPages}
                    </span>
                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                      →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pannello dettaglio */}
        {selectedRichiesta && (
          <div className="lg:w-[520px] xl:w-[580px] 2xl:w-[640px] shrink-0 rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white h-fit max-h-[calc(100vh-200px)] sticky top-4 flex flex-col">
            <PannelloDettaglio
              richiesta={selectedRichiesta}
              token={token}
              onClose={() => { setSelectedId(null); setSelectedRichiesta(null); }}
              onUpdate={handleUpdate}
              onRichiediDocumenti={() => setShowRichiediDocDialog(true)}
              onCondividi={() => setShowCondividiDialog(true)}
              onConfermaIngresso={() => setShowConfermaIngressoDialog(true)}
              onArchivia={handleArchivia}
              onRipristina={handleRipristina}
              canCreateFascicolo={canCreateFascicolo}
              vistaArchiviate={vistaArchiviate}
            />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <NuovaRichiestaDialog
        open={showNuovaDialog}
        onOpenChange={setShowNuovaDialog}
        onCreated={handleCreated}
      />

      <RichiediDocumentiDialog
        open={showRichiediDocDialog}
        onOpenChange={setShowRichiediDocDialog}
        richiesta={selectedRichiesta}
        onSuccess={handleUpdate}
      />

      <CondividiReteDialog
        open={showCondividiDialog}
        onOpenChange={setShowCondividiDialog}
        richiesta={selectedRichiesta}
        onSuccess={handleUpdate}
      />

      <ImportaEmailDialog
        open={showImportaEmailDialog}
        onOpenChange={setShowImportaEmailDialog}
        onImportato={(nuova) => {
          handleCreated(nuova);
        }}
      />

      <ConfermaIngressoDialog
        open={showConfermaIngressoDialog}
        onOpenChange={setShowConfermaIngressoDialog}
        richiesta={selectedRichiesta}
        onSuccess={(payload) => {
          if (payload?.refreshOnly) {
            if (payload.richiesta) setSelectedRichiesta(payload.richiesta);
            fetchRichieste();
            return;
          }
          handleUpdate();
        }}
      />
    </div>
  );
}

